// src/features/WhatIfAnalysis/WhatIfEngine.js
import { MLDataPreprocessor } from '../../utils/mlDataPreprocessor';

export class WhatIfEngine {
  constructor() {
    this.scenarios = new Map();
    this.baseData = [];
    this.currentScenario = null;
  }

  // Завантаження базових даних для аналізу
  async loadBaseData(records) {
    this.baseData = records
      .map(record => MLDataPreprocessor.normalizeRecord(record))
      .filter(record => record !== null && record.dataQuality.isComplete);
    
    return this.baseData;
  }

  // Створення сценаріїв
  createScenario(name, parameters, t) {
    const scenario = {
      id: this.generateId(),
      name,
      parameters,
      createdAt: new Date(),
      status: 'created',
      results: null,
      comparisons: null
    };

    this.scenarios.set(scenario.id, scenario);
    this.currentScenario = scenario.id;

    return scenario;
  }

  // Попередньо визначені сценарії
  getPredefinedScenarios(t) {
    return [
      {
        id: 'optimistic',
        name: t('what_if_optimistic_scenario'),
        description: t('what_if_optimistic_desc'),
        parameters: {
          operatorsCount: { value: 3, change: '+1' },
          shiftEfficiency: { value: 1.2, change: '+20%' },
          resourceOptimization: { value: 0.9, change: '-10%' },
          programMix: 'efficient',
          maintenanceSchedule: 'optimized'
        },
        color: '#4caf50'
      },
      {
        id: 'conservative',
        name: t('what_if_conservative_scenario'),
        description: t('what_if_conservative_desc'),
        parameters: {
          operatorsCount: { value: 2, change: '0' },
          shiftEfficiency: { value: 1.0, change: '0%' },
          resourceOptimization: { value: 1.0, change: '0%' },
          programMix: 'current',
          maintenanceSchedule: 'current'
        },
        color: '#ff9800'
      },
      {
        id: 'resource_saving',
        name: t('what_if_resource_saving_scenario'),
        description: t('what_if_resource_saving_desc'),
        parameters: {
          operatorsCount: { value: 2, change: '0' },
          shiftEfficiency: { value: 1.1, change: '+10%' },
          resourceOptimization: { value: 0.8, change: '-20%' },
          programMix: 'resource_saving',
          maintenanceSchedule: 'aggressive'
        },
        color: '#2196f3'
      },
      {
        id: 'high_capacity',
        name: t('what_if_high_capacity_scenario'),
        description: t('what_if_high_capacity_desc'),
        parameters: {
          operatorsCount: { value: 4, change: '+2' },
          shiftEfficiency: { value: 1.15, change: '+15%' },
          resourceOptimization: { value: 1.0, change: '0%' },
          programMix: 'high_production',
          maintenanceSchedule: 'preventive'
        },
        color: '#9c27b0'
      }
    ];
  }

  // Запуск аналізу сценарію
  async runScenarioAnalysis(scenarioId, t) {
    const scenario = this.scenarios.get(scenarioId);
    if (!scenario) {
      throw new Error('Scenario not found');
    }

    scenario.status = 'running';
    
    try {
      // Симуляція впливу параметрів
      const results = await this.simulateScenario(scenario.parameters);
      
      // Порівняння з базовими даними
      const comparisons = this.compareWithBaseline(results);
      
      // Розрахунок ROI та інших метрик
      const businessImpact = this.calculateBusinessImpact(results, comparisons);
      
      scenario.results = results;
      scenario.comparisons = comparisons;
      scenario.businessImpact = businessImpact;
      scenario.status = 'completed';
      scenario.completedAt = new Date();
      
      return scenario;
    } catch (error) {
      scenario.status = 'error';
      scenario.error = error.message;
      throw error;
    }
  }

  // Симуляція сценарію
  async simulateScenario(parameters) {
    // Використовуємо останні 30 днів як базовий період
    const baselinePeriod = this.baseData.slice(-30);
    if (baselinePeriod.length === 0) {
      throw new Error('Insufficient data for simulation');
    }

    const simulationResults = {
      production: this.simulateProduction(baselinePeriod, parameters),
      resources: this.simulateResourceUsage(baselinePeriod, parameters),
      efficiency: this.simulateEfficiency(baselinePeriod, parameters),
      financial: this.simulateFinancialImpact(baselinePeriod, parameters),
      timeline: this.generateTimeline(parameters)
    };

    return simulationResults;
  }

  // Симуляція виробництва
  simulateProduction(baselineData, parameters) {
    const baselineProduction = this.calculateAverageProduction(baselineData);
    
    // Застосовуємо модифікатори
    const efficiencyMultiplier = parameters.shiftEfficiency?.value || 1.0;
    const operatorsMultiplier = this.calculateOperatorsMultiplier(parameters.operatorsCount?.value || 2);
    const programMixMultiplier = this.calculateProgramMixMultiplier(parameters.programMix);
    
    const simulatedProduction = baselineProduction * efficiencyMultiplier * operatorsMultiplier * programMixMultiplier;
    
    return {
      baseline: baselineProduction,
      simulated: simulatedProduction,
      change: simulatedProduction - baselineProduction,
      changePercent: ((simulatedProduction - baselineProduction) / baselineProduction) * 100,
      dailyBreakdown: this.simulateDailyProduction(baselineData, parameters)
    };
  }

  // Симуляція споживання ресурсів
  simulateResourceUsage(baselineData, parameters) {
    const baselineResources = this.calculateAverageResourceUsage(baselineData);
    const optimizationMultiplier = parameters.resourceOptimization?.value || 1.0;
    
    const simulatedResources = {
      water: {
        baseline: baselineResources.water,
        simulated: baselineResources.water * optimizationMultiplier,
        savings: baselineResources.water * (1 - optimizationMultiplier),
        savingsPercent: (1 - optimizationMultiplier) * 100
      },
      propane: {
        baseline: baselineResources.propane,
        simulated: baselineResources.propane * optimizationMultiplier,
        savings: baselineResources.propane * (1 - optimizationMultiplier),
        savingsPercent: (1 - optimizationMultiplier) * 100
      },
      electricity: {
        baseline: baselineResources.electricity,
        simulated: baselineResources.electricity * optimizationMultiplier,
        savings: baselineResources.electricity * (1 - optimizationMultiplier),
        savingsPercent: (1 - optimizationMultiplier) * 100
      }
    };

    return simulatedResources;
  }

  // Симуляція ефективності
  simulateEfficiency(baselineData, parameters) {
    const baselineEfficiency = this.calculateAverageEfficiency(baselineData);
    const efficiencyMultiplier = parameters.shiftEfficiency?.value || 1.0;
    
    const simulatedEfficiency = baselineEfficiency * efficiencyMultiplier;
    
    return {
      baseline: baselineEfficiency,
      simulated: simulatedEfficiency,
      change: simulatedEfficiency - baselineEfficiency,
      changePercent: ((simulatedEfficiency - baselineEfficiency) / baselineEfficiency) * 100,
      factors: {
        operators: parameters.operatorsCount?.value || 2,
        shiftPattern: parameters.shiftEfficiency?.value || 1.0,
        programOptimization: parameters.programMix || 'current'
      }
    };
  }

  // Фінансовий вплив
  simulateFinancialImpact(baselineData, parameters) {
    const productionImpact = this.simulateProduction(baselineData, parameters);
    const resourceImpact = this.simulateResourceUsage(baselineData, parameters);
    
    // Припущення про вартість (можна зробити конфігурованими)
    const revenuePerKg = 2.5; // € за кг
    const waterCostPerLiter = 0.002; // € за літр
    const propaneCostPerKg = 1.2; // € за кг
    const electricityCostPerKwh = 0.15; // € за кВт-год
    const operatorCostPerShift = 120; // € за зміну
    
    const additionalOperators = Math.max(0, (parameters.operatorsCount?.value || 2) - 2);
    
    const financials = {
      revenue: {
        additional: productionImpact.change * 30 * revenuePerKg, // За 30 днів
        monthly: productionImpact.simulated * 30 * revenuePerKg
      },
      costs: {
        resources: {
          water: resourceImpact.water.savings * 30 * waterCostPerLiter,
          propane: resourceImpact.propane.savings * 30 * propaneCostPerKg,
          electricity: resourceImpact.electricity.savings * 30 * electricityCostPerKwh,
          total: 0 // Розрахуємо нижче
        },
        labor: {
          additional: additionalOperators * 30 * operatorCostPerShift,
          total: (parameters.operatorsCount?.value || 2) * 30 * operatorCostPerShift
        }
      },
      netImpact: 0 // Розрахуємо нижче
    };
    
    // Розрахунок загальних показників
    financials.costs.resources.total = 
      financials.costs.resources.water + 
      financials.costs.resources.propane + 
      financials.costs.resources.electricity;
    
    financials.netImpact = 
      financials.revenue.additional - 
      financials.costs.resources.total - 
      financials.costs.labor.additional;
    
    return financials;
  }

  // Порівняння з базовими даними
  compareWithBaseline(results) {
    return {
      production: {
        improvement: results.production.changePercent,
        significance: this.assessSignificance(results.production.changePercent)
      },
      efficiency: {
        improvement: results.efficiency.changePercent,
        significance: this.assessSignificance(results.efficiency.changePercent)
      },
      resources: {
        water: results.resources.water.savingsPercent,
        propane: results.resources.propane.savingsPercent,
        electricity: results.resources.electricity.savingsPercent,
        overall: (results.resources.water.savingsPercent + results.resources.propane.savingsPercent + results.resources.electricity.savingsPercent) / 3
      },
      financial: {
        roi: this.calculateROI(results.financial),
        paybackPeriod: this.calculatePaybackPeriod(results.financial),
        attractiveness: this.assessFinancialAttractiveness(results.financial)
      }
    };
  }

  // Розрахунок бізнес-впливу
  calculateBusinessImpact(results, comparisons) {
    const impactScore = (
      comparisons.production.improvement * 0.4 +
      comparisons.efficiency.improvement * 0.3 +
      comparisons.resources.overall * 0.2 +
      (comparisons.financial.roi > 0 ? 100 : 0) * 0.1
    );

    return {
      score: Math.min(100, Math.max(0, impactScore)),
      level: this.getImpactLevel(impactScore),
      recommendations: this.generateImpactRecommendations(results, comparisons),
      risks: this.identifyRisks(results, comparisons)
    };
  }

  // Допоміжні методи
  calculateAverageProduction(data) {
    const productions = data.map(d => d.productionFeatures.totalProduction).filter(p => p > 0);
    return productions.length > 0 ? productions.reduce((a, b) => a + b, 0) / productions.length : 0;
  }

  calculateAverageResourceUsage(data) {
    const water = data.map(d => d.resourceFeatures.waterConsumption).filter(w => w > 0);
    const propane = data.map(d => d.resourceFeatures.propaneConsumption).filter(p => p > 0);
    const electricity = data.map(d => d.resourceFeatures.electricityConsumption).filter(e => e > 0);
    
    return {
      water: water.length > 0 ? water.reduce((a, b) => a + b, 0) / water.length : 0,
      propane: propane.length > 0 ? propane.reduce((a, b) => a + b, 0) / propane.length : 0,
      electricity: electricity.length > 0 ? electricity.reduce((a, b) => a + b, 0) / electricity.length : 0
    };
  }

  calculateAverageEfficiency(data) {
    const efficiencies = data.map(d => d.productionFeatures.efficiency).filter(e => e > 0);
    return efficiencies.length > 0 ? efficiencies.reduce((a, b) => a + b, 0) / efficiencies.length : 0;
  }

  calculateOperatorsMultiplier(operatorsCount) {
    // Нелінійна залежність - додавання операторів дає менший приріст
    const baseOperators = 2;
    if (operatorsCount <= baseOperators) return 1.0;
    return 1.0 + (operatorsCount - baseOperators) * 0.3; // Кожен додатковий оператор +30%
  }

  calculateProgramMixMultiplier(programMix) {
    const multipliers = {
      current: 1.0,
      efficient: 1.15,
      resource_saving: 0.95,
      high_production: 1.25
    };
    return multipliers[programMix] || 1.0;
  }

  assessSignificance(changePercent) {
    const absChange = Math.abs(changePercent);
    if (absChange < 5) return 'negligible';
    if (absChange < 15) return 'moderate';
    if (absChange < 30) return 'significant';
    return 'high';
  }

  calculateROI(financials) {
    const investment = financials.costs.labor.additional + 500; // Припущення про інвестиції
    const annualBenefit = financials.netImpact * 12;
    return investment > 0 ? (annualBenefit / investment) * 100 : 0;
  }

  calculatePaybackPeriod(financials) {
    const investment = financials.costs.labor.additional + 500;
    const monthlyBenefit = financials.netImpact;
    return monthlyBenefit > 0 ? investment / monthlyBenefit : Infinity;
  }

  assessFinancialAttractiveness(financials) {
    const roi = this.calculateROI(financials);
    const payback = this.calculatePaybackPeriod(financials);
    
    if (roi > 100 && payback < 6) return 'excellent';
    if (roi > 50 && payback < 12) return 'good';
    if (roi > 25 && payback < 24) return 'moderate';
    return 'poor';
  }

  getImpactLevel(score) {
    if (score >= 80) return 'transformational';
    if (score >= 60) return 'high';
    if (score >= 40) return 'moderate';
    if (score >= 20) return 'low';
    return 'minimal';
  }

  generateImpactRecommendations(results, comparisons) {
    const recommendations = [];
    
    if (comparisons.production.improvement > 20) {
      recommendations.push('Consider scaling this scenario for full implementation');
    }
    
    if (comparisons.resources.overall > 15) {
      recommendations.push('Resource savings are significant - prioritize implementation');
    }
    
    if (comparisons.financial.roi > 50) {
      recommendations.push('High ROI detected - favorable business case');
    }
    
    if (results.financial.netImpact < 0) {
      recommendations.push('Monitor costs closely during implementation');
    }
    
    return recommendations;
  }

  identifyRisks(results, comparisons) {
    const risks = [];
    
    if (comparisons.production.significance === 'high') {
      risks.push('Major production changes may require equipment adjustments');
    }
    
    if (results.efficiency.factors.operators > 3) {
      risks.push('Additional operators may require training and supervision');
    }
    
    if (comparisons.financial.paybackPeriod > 18) {
      risks.push('Long payback period may affect financial viability');
    }
    
    return risks;
  }

  generateTimeline(parameters) {
    const baseTime = 2; // тижні
    let additionalTime = 0;
    
    if (parameters.operatorsCount?.value > 2) additionalTime += 1;
    if (parameters.programMix !== 'current') additionalTime += 2;
    if (parameters.maintenanceSchedule !== 'current') additionalTime += 1;
    
    return {
      implementation: baseTime + additionalTime,
      optimization: additionalTime * 2,
      fullBenefits: baseTime + additionalTime * 3
    };
  }

  simulateDailyProduction(baselineData, parameters) {
    // Спрощена симуляція щоденного виробництва
    return baselineData.slice(-7).map(day => ({
      date: day.date,
      baseline: day.productionFeatures.totalProduction,
      simulated: day.productionFeatures.totalProduction * (parameters.shiftEfficiency?.value || 1.0),
      dayOfWeek: new Date(day.date).getDay()
    }));
  }

  generateId() {
    return `scenario_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

export default WhatIfEngine;